/**
 * Logger Utility
 *
 * Provides environment-aware logging that only outputs in development mode.
 * In production, all logs are suppressed to avoid cluttering the console.
 *
 * Usage:
 *   import { logger } from '@/utils/logger';
 *   logger.debug('Canvas', 'Position switched', { from: 'front', to: 'back' });
 *   logger.info('Upload', 'File uploaded successfully');
 *   logger.warn('Asset', 'Using cached URL');
 *   logger.error('API', 'Request failed', error);
 */

/* eslint-disable no-console */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerOptions {
  /** Enable/disable all logging (overrides isDev check) */
  enabled?: boolean;
  /** Minimum log level to display */
  minLevel?: LogLevel;
  /** Show timestamps in logs */
  showTimestamp?: boolean;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const LOG_COLORS: Record<LogLevel, string> = {
  debug: '#9CA3AF', // gray-400
  info: '#3B82F6', // blue-500
  warn: '#F59E0B', // amber-500
  error: '#EF4444', // red-500
};

const LOG_PREFIXES: Record<LogLevel, string> = {
  debug: '🔍',
  info: 'ℹ️',
  warn: '⚠️',
  error: '❌',
};

class Logger {
  private isDev: boolean;
  private options: Required<LoggerOptions>;

  constructor(options: LoggerOptions = {}) {
    this.isDev = import.meta.env.DEV;
    this.options = {
      enabled: options.enabled ?? true,
      minLevel: options.minLevel ?? 'debug',
      showTimestamp: options.showTimestamp ?? false,
    };
  }

  private shouldLog(level: LogLevel): boolean {
    if (!this.options.enabled) return false;
    if (!this.isDev) return false;
    return LOG_LEVELS[level] >= LOG_LEVELS[this.options.minLevel];
  }

  private formatMessage(
    level: LogLevel,
    module: string,
    message: string
  ): string[] {
    const prefix = LOG_PREFIXES[level];
    const timestamp = this.options.showTimestamp
      ? `[${new Date().toISOString().slice(11, 23)}] `
      : '';
    return [
      `%c${prefix} ${timestamp}[${module}] ${message}`,
      `color: ${LOG_COLORS[level]}; font-weight: bold;`,
    ];
  }

  /**
   * Debug level logging - for detailed debugging information
   */
  debug(module: string, message: string, ...data: unknown[]): void {
    if (!this.shouldLog('debug')) return;
    const [formatted, style] = this.formatMessage('debug', module, message);
    if (data.length > 0) {
      console.log(formatted, style, ...data);
    } else {
      console.log(formatted, style);
    }
  }

  /**
   * Info level logging - for general information
   */
  info(module: string, message: string, ...data: unknown[]): void {
    if (!this.shouldLog('info')) return;
    const [formatted, style] = this.formatMessage('info', module, message);
    if (data.length > 0) {
      console.info(formatted, style, ...data);
    } else {
      console.info(formatted, style);
    }
  }

  /**
   * Warn level logging - for warnings that don't stop execution
   */
  warn(module: string, message: string, ...data: unknown[]): void {
    if (!this.shouldLog('warn')) return;
    const [formatted, style] = this.formatMessage('warn', module, message);
    if (data.length > 0) {
      console.warn(formatted, style, ...data);
    } else {
      console.warn(formatted, style);
    }
  }

  /**
   * Error level logging - for errors
   */
  error(module: string, message: string, ...data: unknown[]): void {
    if (!this.shouldLog('error')) return;
    const [formatted, style] = this.formatMessage('error', module, message);
    if (data.length > 0) {
      console.error(formatted, style, ...data);
    } else {
      console.error(formatted, style);
    }
  }

  /**
   * Group logs together (collapsible in console)
   */
  group(module: string, label: string, collapsed = true): void {
    if (!this.isDev || !this.options.enabled) return;
    if (collapsed) {
      console.groupCollapsed(`[${module}] ${label}`);
    } else {
      console.group(`[${module}] ${label}`);
    }
  }

  /**
   * End a log group
   */
  groupEnd(): void {
    if (!this.isDev || !this.options.enabled) return;
    console.groupEnd();
  }

  /**
   * Log a table (useful for arrays/objects)
   */
  table(module: string, data: unknown): void {
    if (!this.isDev || !this.options.enabled) return;
    console.log(`[${module}] Data:`);
    console.table(data);
  }

  /**
   * Time a operation
   */
  time(label: string): void {
    if (!this.isDev || !this.options.enabled) return;
    console.time(label);
  }

  /**
   * End timing and log result
   */
  timeEnd(label: string): void {
    if (!this.isDev || !this.options.enabled) return;
    console.timeEnd(label);
  }
}

// Export singleton instance
export const logger = new Logger();

// Export class for custom instances
export { Logger };
export type { LogLevel, LoggerOptions };
